import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePrevious } from 'react-use';
import {
  CacheWriteType,
  useCacheWriterBindingLazy,
} from '../../../../../../api/hooks/binding/cache-writer.binding';
import { useScriptActionBindingLazy } from '../../../../../../api/hooks/binding/script-action.binding';
import { ProgramState } from '../../../../../../components/machine/machine-state';
import { ApplicationConfig } from '../../../../../../config/config';
import { useMachineContext } from '../../../../../../pages/machine/machine-context-provider';
import { Waypoint } from '../../../../../../util/machine-variable.utils';
import { PartUtil } from '../../../../../../util/part.util';
import { Pose } from '../../../../../../util/pose.util';

interface FineTuneScriptProps {
  onProgramStop?: () => void;
  tcp: Waypoint;
  executingIndex: number;
}

interface FineTuneScriptResponse {
  moveToPosition: (pose: Pose) => Promise<void>;
}

interface FineTuneScriptLazyResponse {
  executeFineTune: () => Promise<boolean>;
  moveToPosition: (pose: Pose) => Promise<void>;
  isReady: boolean;
  close: () => void;
}

const useFineTuneScript = (
  props: FineTuneScriptProps
): FineTuneScriptResponse => {
  const { executeFineTune, moveToPosition } = useFineTuneScriptLazy(props);

  useEffect(() => {
    // Automatically execute the fine tune script on mount
    executeFineTune();
  }, [executeFineTune]);

  const response = useMemo<FineTuneScriptResponse>(() => {
    return {
      moveToPosition,
    };
  }, [moveToPosition]);

  return response;
};

/**
 * Lazy version of useFineTuneScript that only executes the script when explicitly called.
 * This is useful when you want to control when the fine tune script starts (e.g., on button press).
 */
const useFineTuneScriptLazy = (
  props: FineTuneScriptProps
): FineTuneScriptLazyResponse => {
  const { onProgramStop, tcp, executingIndex } = props;

  const { machine } = useMachineContext();
  const [scriptExecutionFailed, setScriptExecutionFailed] =
    useState<boolean>(false);
  const [isReady, setIsReady] = useState<boolean>(false);
  const hasSetupScript = useRef<boolean>(false);
  const writeToCache = useCacheWriterBindingLazy();

  const { send: sendScriptLazy, close: closeScriptBinding } =
    useScriptActionBindingLazy({
      closeImmediately: false,
    });

  const programState = machine.state?.transient?.status?.programState;
  const previousProgramState = usePrevious(programState);

  // Handle program stop events
  useEffect(() => {
    if (!scriptExecutionFailed) {
      if (
        [ProgramState.PROGRAM_PLAYING, ProgramState.PROGRAM_PAUSED].includes(
          previousProgramState
        ) &&
        programState === ProgramState.PROGRAM_STOPPED
      ) {
        if (typeof onProgramStop === 'function') {
          onProgramStop();
        }
        // Reset the script setup flag so it can be executed again
        hasSetupScript.current = false;
        setIsReady(false);
      }
    }
  }, [
    previousProgramState,
    programState,
    scriptExecutionFailed,
    onProgramStop,
  ]);

  // Initialize cache writer binding
  useEffect(() => {
    writeToCache(machine?.id, {
      requests: [],
    });
  }, [writeToCache, machine?.id]);

  const executeFineTune = useCallback(async (): Promise<boolean> => {
    const currentProgramState =
      machine.state?.transient?.status?.programState ??
      ProgramState.PROGRAM_STOPPED;

    const shouldSendFineTuneScript = [
      ProgramState.PROGRAM_STOPPED,
      ProgramState.PROGRAM_STOPPING,
    ].includes(currentProgramState);

    if (hasSetupScript.current || !shouldSendFineTuneScript) {
      return false;
    }

    if (!tcp) {
      return false;
    }

    try {
      setScriptExecutionFailed(false);
      const script = getFineTuneScript(tcp, executingIndex);

      const result = await sendScriptLazy(machine.id, {
        script,
        onStop: {
          validProgramStates: ['PROGRAM_PLAYING', 'PROGRAM_PAUSED'],
          script: getFineTuneHaltScript(),
        },
      });

      if (result?.success) {
        hasSetupScript.current = true;
        setIsReady(true);
        return true;
      } else {
        hasSetupScript.current = false;
        setScriptExecutionFailed(true);
        setIsReady(false);
        return false;
      }
    } catch (error) {
      hasSetupScript.current = false;
      setScriptExecutionFailed(true);
      setIsReady(false);
      return false;
    }
  }, [
    machine.state?.transient?.status?.programState,
    machine.id,
    tcp,
    executingIndex,
    sendScriptLazy,
  ]);

  const moveToPosition = useCallback(
    async (pose1: Pose) => {
      const poses = [PartUtil.convertPoseToWaypoint(pose1)];

      await writeToCache(machine.id, {
        requests: [
          {
            path: 'finetune.poses',
            value: poses,
            type: CacheWriteType.MEMORY,
          },
        ],
      });
    },
    [writeToCache, machine?.id]
  );

  const close = useCallback(() => {
    // Reset internal state before closing the binding
    hasSetupScript.current = false;
    setIsReady(false);
    setScriptExecutionFailed(false);

    // Close the script binding
    closeScriptBinding();
  }, [closeScriptBinding]);

  const response = useMemo<FineTuneScriptLazyResponse>(() => {
    return {
      executeFineTune,
      moveToPosition,
      isReady,
      close,
    };
  }, [executeFineTune, moveToPosition, isReady, close]);

  return response;
};

const getFineTuneScript = (tcp: Waypoint, executingIndex: number): string => {
  const convertedTcp = PartUtil.convertWaypointToScriptPose(tcp as Waypoint);

  const url = new URL(
    `http://${ApplicationConfig.robot.rpc.host}:${ApplicationConfig.robot.rpc.port}`
  );

  url.pathname = ApplicationConfig.robot.rpc.path;

  const script = [
    `def fine_tune():`,
    ` local LIVE_EXECUTION_REG = 24`,
    ` local client = rpc_factory("xmlrpc", "${url}")`,
    ` def moveTo(tcp, liveExecutionStep):`,
    `   set_tcp(tcp)`,
    ` 	write_output_integer_register(LIVE_EXECUTION_REG, -1)`,
    `   local prevHaltState = False`,
    `   local defaultPoses = [p[0, 0, 0, 0, 0, 0]]`,
    `   local previousPoses = defaultPoses`,
    `   client.setCacheValue("finetune.halt", prevHaltState, False)`,
    `   client.setCacheValue("finetune.poses", previousPoses, False)`,
    `   while (True):`,
    `     if (get_tool_digital_in(0)):`,
    `       freedrive_mode()`,
    `       set_tool_digital_out(0, False)`,
    `       while (get_tool_digital_in(0)):`,
    `         sync()`,
    `       end`,
    `       end_freedrive_mode()`,
    `       set_tool_digital_out(0, True)`,
    `     end`,
    `     local cachedPoses = client.getCacheValue("finetune.poses", previousPoses)`,
    `     if (cachedPoses != previousPoses and cachedPoses[0] != defaultPoses[0]):`,
    `       previousPoses = cachedPoses`,
    `       movel(cachedPoses[0], a=0.05, v=0.05, t=0, r=0)`,
    `       stopl(10)`,
    `     end`,
    `     local cachedHalt = client.getCacheValue("finetune.halt", prevHaltState)`,
    `     if (cachedHalt != prevHaltState):`,
    `       halt`,
    `     end`,
    `     sleep(.1)`,
    `   end`,
    ` end`,
    ` moveTo(${convertedTcp}, ${executingIndex})`,
    `end`,
  ];

  return script.join('\n');
};

const getFineTuneHaltScript = () => {
  return getHaltScript('fine_tune');
};

const getHaltScript = (scriptName: string) => {
  return [`def ${scriptName}():`, `halt`, `end`].join('\n');
};

export { getFineTuneScript, useFineTuneScript, useFineTuneScriptLazy };
